import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get } from "../../_services/apiService";
import { API_URL } from "../../_services/apiUrl";
import toast from "react-hot-toast";
import Layout from "../Shared/Layout";
import "../../ComponentCss/cancelorder.css";
import { FaFileUpload } from "react-icons/fa";
import { ReturnAndCancelType } from "../../utils/staticData";
import { useWishlist } from "../../context/wishlistContext";

const CancelOrder = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { ipAddress } = useWishlist();

    // URL PARAM
    const { id } = useParams();

    // NORMAL + ENCRYPTED BOTH SUPPORT
    const orderId = isNaN(Number(id)) ? id : Number(id);

    // ACTION TYPE
    const actionType =
        location.state?.actionType === "return"
            ? "return"
            : "cancel";

    // STATES
    const [orderItems, setOrderItems] = useState([]);
    const [orderInfo, setOrderInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const [reason, setReason] = useState(null);
    const [comment, setComment] = useState("");
    const [images, setImages] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // FILTER REASON
    const filteredReasonTypes = ReturnAndCancelType.filter(
        (item) => item.type === actionType
    );

    const parseProductDetails = useCallback((value) => {
        if (!value) return {};
        if (typeof value === "object") return value;

        try {
            return JSON.parse(value);
        } catch (error) {
            return {};
        }
    }, []);

    const extractOrderDetails = useCallback((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.details)) return response.details;
        if (Array.isArray(response?.order?.details)) return response.order.details;
        if (Array.isArray(response?.data?.details)) return response.data.details;
        return [];
    }, []);

    const extractOrderInfo = useCallback((response, details = []) => {
        return (
            details?.[0]?.order ||
            response?.order ||
            response?.data?.order ||
            response?.data ||
            null
        );
    }, []);

    // ORDER DETAILS API
    const OrderDetailById = useCallback(async () => {
        if (!orderId) return;

        try {
            setLoading(true);

            const params = {
                order_id: orderId,
            };

            if (ipAddress) {
                params.ip_address = ipAddress;
            }

            const response = await get(API_URL.orderDetailById, params);
            const data = extractOrderDetails(response);
            const order = extractOrderInfo(response, data);

            if (data.length > 0 || order?.id) {
                setOrderItems(
                    data.map((item) => ({
                        ...item,
                        product_details: parseProductDetails(item?.product_details),
                    }))
                );

                setOrderInfo(order);
            } else {
                setOrderItems([]);
                setOrderInfo(null);
            }
        } catch (error) {
            toast.error("Failed to load order");
        } finally {
            setLoading(false);
        }
    }, [extractOrderDetails, extractOrderInfo, ipAddress, orderId, parseProductDetails]);

    // INITIAL LOAD
    useEffect(() => {
        if (!orderId) {
            toast.error("Invalid Order ID");
            return;
        }

        OrderDetailById();
    }, [OrderDetailById, orderId]);

    // SELECT REASON
    const handleReasonSelect = (item) => {
        setReason(item);

        // cancel pe images remove
        if (item?.type !== "return") {
            setImages([]);
        }
    };

    // FILE CHANGE
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);

        if (!files.length) return;

        const validFiles = [];

        const maxSize = 5 * 1024 * 1024;

        files.forEach((file) => {
            if (file.size > maxSize) {
                toast.error(
                    `${file.name} size must be less than 5MB`
                );
            } else {
                validFiles.push({
                    file,
                    preview: URL.createObjectURL(file),
                });
            }
        });

        setImages((prev) => [...prev, ...validFiles]);

        e.target.value = "";
    };

    // REMOVE IMAGE
    const removeImage = (index) => {
        setImages((prev) => {
            const updated = [...prev];

            const removed = updated[index];

            if (removed?.preview) {
                URL.revokeObjectURL(removed.preview);
            }

            updated.splice(index, 1);

            return updated;
        });
    };

    // CLEAR PREVIEW MEMORY
    useEffect(() => {
        return () => {
            images.forEach((img) => {
                if (img?.preview) {
                    URL.revokeObjectURL(img.preview);
                }
            });
        };
    }, [images]);

    const validateCancelRequest = () => {
        if (!reason) {
            toast.error("Please select a reason");
            return false;
        }

        if (!orderInfo?.id) {
            toast.error("Order ID not found");
            return false;
        }

        return true;
    };

    const submitCancelRequest = async () => {
        try {
            if (!validateCancelRequest()) {
                return;
            }

            setSubmitting(true);

            const formData = new FormData();

            formData.append("order_id", orderInfo?.id);

            formData.append("type", reason?.type);

            formData.append(
                "reason",
                reason?.reason || ""
            );

            formData.append(
                "comment",
                comment || ""
            );

            // RETURN FILES
            if (
                reason?.type === "return" &&
                images.length > 0
            ) {
                images.forEach((img) => {
                    formData.append(
                        "return_file",
                        img.file
                    );
                });
            }

            const response = await fetch(
                "https://mugemsapi.lifecode.co.in/api/v1/order/cancel-return",
                {
                    method: "POST",
                    body: formData,
                }
            );

            const data = await response.json();

            if (response.ok) {
                // RESET
                setReason(null);
                setComment("");
                setImages([]);
                setShowConfirmModal(false);
                setSuccessMessage(
                    data?.message ||
                    (actionType === "return"
                        ? "Return request submitted successfully"
                        : "Order canceled successfully")
                );

                setTimeout(() => {
                    navigate("/myorder", {
                        state: {
                            activeTab: "orders",
                            orderFilter: "cancelled",
                        },
                    });
                }, 1000);
            } else {
                toast.error(
                    data?.message ||
                    "Something went wrong"
                );
            }
        } catch (error) {

            toast.error("Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    // SUBMIT
    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateCancelRequest()) {
            return;
        }

        setShowConfirmModal(true);
    };

    // LOADER
    if (loading) {
        return (
            <Layout
                pageTitle={
                    actionType === "return"
                        ? "Return Order"
                        : "Cancel Order"
                }
                breadcrumbLabel={
                    actionType === "return"
                        ? "Return Order"
                        : "Cancel Order"
                }
            >
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                </div>
            </Layout>
        );
    }

    // NO ORDER
    if (!orderInfo) {
        return (
            <Layout
                pageTitle="No Order Found"
                breadcrumbLabel="No Order Found"
            >
                <div className="text-center py-5">
                    <h3>No Order Found</h3>
                </div>
            </Layout>
        );
    }

    return (
        <>
            <Layout
                pageTitle={
                    actionType === "return"
                        ? "Return Order"
                        : "Cancel Order"
                }
                breadcrumbLabel={
                    actionType === "return"
                        ? "Return Order"
                        : "Cancel Order"
                }
            >
                <div className="cancel_corder_main pt-sm-4 pt-2 pb-sm-5 pb-2 d-flex justify-content-center">
                    <div className="col-xl-6 col-md-8 col-sm-10 col-11">

                        {/* PRODUCTS */}
                        {orderItems.map((item) => (
                            <div
                                className="product-card mb-3"
                                key={item.id}
                            >
                                <img
                                    src={
                                        item?.product_all_status
                                            ?.thumbnail_full_url ||
                                        "https://picsum.photos/500"
                                    }
                                    className="product-image"
                                    alt={
                                        item?.product_details
                                            ?.name || "product"
                                    }
                                />

                                <div className="product-details">
                                    <h4>
                                        {item?.product_details
                                            ?.name ||
                                            "Product Name"}
                                    </h4>

                                    <p>
                                        Order ID :
                                        #
                                        {item?.order_id ||
                                            orderInfo?.id}
                                    </p>

                                    <p className="price">
                                        ₹{item?.price || 0}
                                    </p>
                                </div>
                            </div>
                        ))}

                        {/* FORM */}
                        <form onSubmit={handleSubmit}>
                            <div className="cancel_order_form">

                                {/* REASONS */}
                                <h5>
                                    Why are you{" "}
                                    {actionType === "return"
                                        ? "returning"
                                        : "cancelling"}{" "}
                                    this order?
                                </h5>

                                <select
                                    className="form-select mt-3"
                                    value={reason?.id || ""}
                                    onChange={(e) => {
                                        const selectedReason = filteredReasonTypes.find(
                                            (item) => item.id === Number(e.target.value)
                                        );

                                        handleReasonSelect(selectedReason);
                                    }}
                                >
                                    <option value="">
                                        Select Reason
                                    </option>

                                    {filteredReasonTypes.map((item) => (
                                        <option
                                            key={item.id}
                                            value={item.id}
                                        >
                                            {item.reason}
                                        </option>
                                    ))}
                                </select>

                                {/* IMAGE UPLOAD */}
                                {reason?.type === "return" && (
                                    <div className="mt-4">
                                        <h5>
                                            Upload Images
                                        </h5>

                                        <div className="file-upload-container">
                                            <div>
                                                <label
                                                    htmlFor="filePath"
                                                    className="file-upload-label text-center"
                                                >
                                                    <p className="upload-icon mb-1">
                                                        <FaFileUpload
                                                            size={
                                                                24
                                                            }
                                                        />
                                                    </p>

                                                    <span className="upload-text">
                                                        Select
                                                        Images
                                                    </span>

                                                    {images.length <=
                                                        0 && (
                                                            <p className="mb-0 mt-1 message_info">
                                                                Upload
                                                                images.
                                                                Max
                                                                size
                                                                5MB.
                                                            </p>
                                                        )}
                                                </label>

                                                <input
                                                    type="file"
                                                    onChange={
                                                        handleFileChange
                                                    }
                                                    className="file-input"
                                                    name="filePath"
                                                    accept="image/*"
                                                    id="filePath"
                                                    multiple
                                                    style={{
                                                        display:
                                                            "none",
                                                    }}
                                                />

                                                {images.length >
                                                    0 && (
                                                        <div className="selected-files-count text-center mt-2">
                                                            <p>
                                                                {
                                                                    images.length
                                                                }{" "}
                                                                file(s)
                                                                selected
                                                            </p>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>

                                        {/* PREVIEW */}
                                        <div className="preview-grid mt-3">
                                            {images.map(
                                                (
                                                    img,
                                                    index
                                                ) => (
                                                    <div
                                                        className="preview-card"
                                                        key={
                                                            index
                                                        }
                                                    >
                                                        <img
                                                            src={
                                                                img.preview
                                                            }
                                                            alt={`preview-${index}`}
                                                            className="preview-image"
                                                        />

                                                        <button
                                                            type="button"
                                                            className="remove-img"
                                                            onClick={() =>
                                                                removeImage(
                                                                    index
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* COMMENT */}
                                <div className="cancel_description d-block mt-4">
                                    <h5>
                                        Additional
                                        Comments
                                    </h5>

                                    <div>
                                        <textarea
                                            value={comment}
                                            onChange={(e) =>
                                                setComment(
                                                    e.target
                                                        .value
                                                )
                                            }
                                            placeholder="Enter your comment"
                                        />
                                    </div>
                                </div>

                                {/* BUTTONS */}
                                <div className="d-flex gap-3 mt-4">
                                    <button
                                        type="submit"
                                        className="cancel_order_submit_btn"
                                        disabled={
                                            submitting
                                        }
                                    >
                                        {submitting
                                            ? "Submitting..."
                                            : actionType ===
                                                "return"
                                                ? "Confirm Return"
                                                : "Confirm Cancel"}
                                    </button>

                                    <button
                                        type="button"
                                        className="cancel_order_submit_btn"
                                        onClick={() =>
                                            navigate(-1)
                                        }
                                    >
                                        Keep Order
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {showConfirmModal && (
                    <div className="cancel-confirm-overlay" role="presentation">
                        <div
                            className="cancel-confirm-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="cancel-confirm-title"
                        >
                            <h4 id="cancel-confirm-title">
                                {actionType === "return"
                                    ? "Confirm Return Request"
                                    : "Confirm Cancel Order"}
                            </h4>

                            <p>
                                {actionType === "return"
                                    ? "Are you sure you want to submit this return request?"
                                    : "Are you sure you want to cancel this order?"}
                            </p>

                            {reason?.reason && (
                                <p className="cancel-confirm-reason">
                                    Reason: {reason.reason}
                                </p>
                            )}

                            <div className="cancel-confirm-actions">
                                <button
                                    type="button"
                                    className="cancel-confirm-btn cancel-confirm-yes"
                                    onClick={submitCancelRequest}
                                    disabled={submitting}
                                >
                                    {submitting ? "Submitting..." : "Yes"}
                                </button>

                                <button
                                    type="button"
                                    className="cancel-confirm-btn cancel-confirm-no"
                                    onClick={() => setShowConfirmModal(false)}
                                    disabled={submitting}
                                >
                                    No
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {successMessage && (
                    <div className="cancel-confirm-overlay" role="presentation">
                        <div
                            className="cancel-confirm-modal cancel-success-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="cancel-success-title"
                        >
                            <div className="cancel-success-icon">✓</div>

                            <h4 id="cancel-success-title">
                                {actionType === "return"
                                    ? "Return Request Submitted"
                                    : "Order Cancelled"}
                            </h4>

                            <p>{successMessage}</p>
                        </div>
                    </div>
                )}
            </Layout>
        </>
    );
};

export default CancelOrder;
